/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useTranslation as useT } from "react-i18next";
import { i18n as i18nType, TFunction } from "i18next";
import { cultures } from "../languages";
import { ermittleWaehrung } from "../waehrung";
import useValueTranslation, { TValueFunction } from "./useValueTranslation";

export interface UseTranslationResponse extends Partial<FormatParts> {
    t: TFunction;
    tValue: TValueFunction;
    ready: boolean;
    i18n: i18nType;
}

export interface UseTranslationOptions {
    withFormatParts?: boolean;
}

export default function useTranslation(
    namespace: string,
    options?: UseTranslationOptions
): UseTranslationResponse {
    const { t, ready, i18n } = useT(namespace);
    const tValue = useValueTranslation();

    let formatParts: Partial<FormatParts> = {};
    if (!!options?.withFormatParts) {
        formatParts = getFormatParts(i18n.language ?? cultures.enGB);
    }

    return { t, tValue, ready, i18n, ...formatParts };
}

interface FormatParts {
    currencySymbol: string;
    currencySymbolPlacement: "prefix" | "suffix";
    thousandSeparator: string;
    decimalSeparator: string;
}

function getFormatParts(locale: string): FormatParts {
    const numberGreaterThanThousand = 4200;

    if (!(Object.values(cultures).some(culture => culture.startsWith(locale)))) {
        locale = cultures.enGB;
    }

    const parts = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: ermittleWaehrung(),
    }).formatToParts(numberGreaterThanThousand);

    return {
        currencySymbol: parts.find((p) => p.type === "currency")?.value ?? "",
        currencySymbolPlacement: parts[0].type === "currency" ? "prefix" : "suffix",
        thousandSeparator: parts.find((p) => p.type === "group")?.value ?? "",
        decimalSeparator: parts.find((p) => p.type === "decimal")?.value ?? "",
    };
}
